import { useState } from "react";
import { CalendarDays, Users, CreditCard } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { packages, availableSlots } from "../../data/mock";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingDialog = ({ open, onOpenChange, selectedPackage }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    packageId: selectedPackage || "couple",
    seats: 2,
    requests: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "packageId" && value === "couple") {
      setFormData((prev) => ({ ...prev, seats: 2 }));
    }
    if (field === "packageId" && value === "group") {
      setFormData((prev) => ({ ...prev, seats: 4 }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      // Send data directly to backend

      const res = await axios.post(`${API}/bookings`, formData);

      if (res.data.status === "success") {
        setBookingResult({
          ...formData,
          bookingId: res.data.booking_id,
          totalAmount: res.data.totalAmount, // Optional: if returned by backend
        });
        setIsSuccess(true);
        toast.success("Booking request sent successfully!");
      }
    } catch (err) {
      const msg =
        err.response?.data?.error || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSuccess(false);
    setBookingResult(null);
    setFormData({
      name: "",
      phone: "",
      email: "",
      date: "",
      packageId: "couple",
      seats: 2,
      requests: "",
    });
    onOpenChange(false);
  };

  const selectedPkg = packages.find((p) => p.id === formData.packageId);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-obsidian-light border-amber/[0.08] text-ivory max-w-lg max-h-[90vh] overflow-y-auto p-0 rounded-sm">
        <DialogHeader className="p-7 pb-0">
          <DialogTitle className="font-heading text-2xl font-light text-ivory">
            {isSuccess ? "Booking Confirmed" : "Reserve Your Evening"}
          </DialogTitle>
        </DialogHeader>

        {isSuccess ? (
          <div className="p-7 pt-5 text-center">
            <div className="w-14 h-14 rounded-full border border-amber/15 flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-6 h-6 text-amber"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="font-heading text-xl font-light text-ivory mb-3">
              Booking Received!
            </h3>
            <p className="font-body text-[14px] text-ivory/30 mb-4 leading-relaxed font-light">
              We have received your request. Our team will contact you shortly
              to finalize your experience.
            </p>
            {bookingResult && (
              <div className="bg-obsidian/60 border border-amber/[0.08] rounded-sm p-4 mb-6 text-left">
                <p className="font-body text-[11px] text-ivory/30 uppercase tracking-wider mb-2">
                  Reference ID: {bookingResult.bookingId}
                </p>
                <p className="font-body text-[13px] text-ivory/60">
                  <span className="text-ivory/30">Package:</span>{" "}
                  {packages.find((p) => p.id === bookingResult.packageId)?.name}
                </p>
                <p className="font-body text-[13px] text-ivory/60">
                  <span className="text-ivory/30">Slot:</span>{" "}
                  {bookingResult.date}
                </p>
              </div>
            )}
            <button
              onClick={handleClose}
              className="font-body text-[12px] font-medium tracking-[0.12em] uppercase px-8 py-3 bg-amber/90 text-obsidian rounded-full hover:bg-amber transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-7 pt-3 space-y-5">
            {/* ... Name Field ... */}
            <div>
              <label className="font-body text-[11px] text-ivory/30 mb-2 block tracking-wider uppercase">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Enter your name"
                required
                className="w-full font-body text-[14px] bg-obsidian/60 border border-amber/[0.08] rounded-sm px-4 py-3 text-ivory focus:outline-none focus:border-amber/20 transition-colors font-light"
              />
            </div>

            {/* Phone + Email */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-body text-[11px] text-ivory/30 mb-2 block tracking-wider uppercase">
                  Phone *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  placeholder="+91 XXXXX XXXXX"
                  required
                  className="w-full font-body text-[14px] bg-obsidian/60 border border-amber/[0.08] rounded-sm px-4 py-3 text-ivory focus:outline-none"
                />
              </div>
              <div>
                <label className="font-body text-[11px] text-ivory/30 mb-2 block tracking-wider uppercase">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="email@example.com"
                  className="w-full font-body text-[14px] bg-obsidian/60 border border-amber/[0.08] rounded-sm px-4 py-3 text-ivory focus:outline-none"
                />
              </div>
            </div>

            {/* Slot Selection */}
            <div>
              <label className="font-body text-[11px] text-ivory/30 mb-2 block tracking-wider uppercase">
                <CalendarDays size={10} className="inline mr-1.5" /> Preferred
                Slot *
              </label>
              <select
                value={formData.date}
                onChange={(e) => handleChange("date", e.target.value)}
                required
                className="w-full font-body text-[14px] bg-obsidian/60 border border-amber/[0.08] rounded-sm px-4 py-3 text-ivory appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23C8956D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 16px center",
                }}
              >
                <option value="" disabled>
                  Select a slot
                </option>
                {availableSlots.map((slot) => (
                  <option
                    key={slot.value}
                    value={slot.value}
                    className="bg-obsidian text-ivory"
                  >
                    {slot.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Package Selection */}
            <div>
              <label className="font-body text-[11px] text-ivory/30 mb-2 block tracking-wider uppercase">
                <CreditCard size={10} className="inline mr-1.5" /> Package
              </label>
              <div className="grid grid-cols-3 gap-2">
                {packages.map((pkg) => (
                  <button
                    key={pkg.id}
                    type="button"
                    onClick={() => handleChange("packageId", pkg.id)}
                    className={`font-body text-[11px] py-3 px-2 rounded-sm border transition-all duration-300 ${
                      formData.packageId === pkg.id
                        ? "border-amber/25 bg-amber/[0.06] text-amber"
                        : "border-amber/[0.06] text-ivory/30"
                    }`}
                  >
                    <span className="block font-medium">{pkg.name}</span>
                    <span className="block text-[9px] mt-0.5 opacity-50">
                      {pkg.priceLabel}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Seat section */}

            {formData.packageId === "private" && (
              <div>
                <label className="font-body text-[11px] text-ivory/30 mb-2 block tracking-wider uppercase">
                  <Users size={10} className="inline mr-1.5" /> Number of Seats
                </label>
                <input
                  type="number"
                  min={1}
                  max={4}
                  value={formData.seats}
                  onChange={(e) => handleChange("seats", e.target.value)}
                  className="w-full font-body text-[14px] bg-obsidian/60 border border-amber/[0.08] rounded-sm px-4 py-3 text-ivory focus:outline-none"
                />
              </div>
            )}

            {/* Total Display */}
            {selectedPkg && (
              <div className="border border-amber/[0.06] rounded-sm p-5 flex items-center justify-between">
                <span className="font-body text-[13px] text-ivory/30 font-light">
                  Estimated Total
                </span>
                <span className="font-body text-2xl font-semibold text-amber tracking-tight">
                  ₹
                  {(formData.packageId === "private"
                    ? selectedPkg.price * formData.seats
                    : selectedPkg.price
                  ).toLocaleString("en-IN")}
                </span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-shimmer font-body text-[12px] font-medium tracking-[0.12em] uppercase py-4 bg-amber/90 text-obsidian rounded-full hover:bg-amber transition-colors disabled:opacity-40"
            >
              {isSubmitting ? "Confirming..." : "Confirm Booking"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
